// WP-A · validation: E.164 normalization, night math (6–28 gate, D19, plus the
// 5-night gap-fill exception), ISO date
// helpers, and per-form field validators. Pure functions so they unit-test
// cleanly (Tier 2) and so the security rules mirror the exact same bounds.
import { MIN_NIGHTS, MAX_NIGHTS, GAP_FILL_NIGHTS, MAX_GUESTS, MAX_ADVANCE_DAYS, PAYMENT_CHANNELS, CALLBACK_PREFS, AGES } from "../config.js";

// --- phone (E.164) ----------------------------------------------------------

// Accepts common US/INTL formats; normalizes to +<country><number>. US/CA numbers
// (no country code, 10 digits) default to +1 (the villa is USVI/US). Returns the
// E.164 string or null if unnormalizable.
export function normalizeE164(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  // Strip spaces, dashes, dots, parens, and a leading "+" we'll re-add.
  const hadPlus = s.startsWith("+");
  s = s.replace(/[()\-\s.]/g, "");
  if (!hadPlus) {
    // Bare US/USVI number → +1. 10 digits (area+number) → prepend country code 1.
    // 11 digits starting with 1 → already has the country code; keep it.
    if (/^\d{10}$/.test(s)) s = "1" + s;
  } else {
    s = s.replace(/^\+/, "");
  }
  const e164 = "+" + s;
  return isE164(e164) ? e164 : null;
}

// E.164 per ITU: + then 6–15 digits, first digit of country code 1–9.
export function isE164(v) {
  return typeof v === "string" && /^\+[1-9]\d{6,14}$/.test(v);
}

// --- dates / nights ---------------------------------------------------------

export function toISODate(d) {
  if (!d) return null;
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function isISODate(v) {
  return typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));
}

// Whole nights between two ISO dates (checkout minus checkin), ignoring time.
export function computeNights(checkinISO, checkoutISO) {
  if (!isISODate(checkinISO) || !isISODate(checkoutISO)) return null;
  const a = Date.parse(checkinISO + "T00:00:00Z");
  const b = Date.parse(checkoutISO + "T00:00:00Z");
  const ms = b - a;
  if (ms <= 0) return null;
  return Math.round(ms / 86400000);
}

export function inNightRange(nights) {
  return Number.isInteger(nights) && nights >= MIN_NIGHTS && nights <= MAX_NIGHTS;
}

// Gap-fill exception (session 66): exactly GAP_FILL_NIGHTS nights that fill the
// whole hole between two bookings — check-in on a booked range's end (its
// checkout day) AND check-out on a booked range's start. `booked` is the
// availability mirror's [{start, end}] list (end exclusive). Overlap with a
// booking INSIDE the range is isRangeAvailable's job, not this one's.
export function isGapFill(booked, checkinISO, checkoutISO) {
  if (!Array.isArray(booked) || computeNights(checkinISO, checkoutISO) !== GAP_FILL_NIGHTS) return false;
  return booked.some((r) => r.end === checkinISO) && booked.some((r) => r.start === checkoutISO);
}

// The one stay-length gate: 6–28 nights (D19), or an exact 5-night gap fill.
// Without `booked` (no availability data) only the plain range applies.
export function isStayLengthOk(checkinISO, checkoutISO, booked) {
  const n = computeNights(checkinISO, checkoutISO);
  return inNightRange(n) || isGapFill(booked, checkinISO, checkoutISO);
}

export const GAP_FILL_HINT = `${GAP_FILL_NIGHTS}-night stays only when they exactly fill the gap between two bookings`;

/**
 * True if `date` is beyond the booking horizon (today + MAX_ADVANCE_DAYS).
 * Dates past the horizon are unselectable in the availability calendar —
 * pricing isn't set that far out, and 18 months matches VRBO + the cleaner
 * schedule (one consistent timeframe, session 34).
 */
export function isBeyondAdvanceWindow(date, today, maxDays = MAX_ADVANCE_DAYS) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const cap = new Date(today);
  cap.setHours(0, 0, 0, 0);
  cap.setDate(cap.getDate() + maxDays);
  return d.getTime() > cap.getTime();
}

export function isEmail(v) {
  return typeof v === "string" && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
}

export function isNonEmptyString(v) {
  return typeof v === "string" && v.trim().length > 0;
}

export function isInt(v, min = -Infinity, max = Infinity) {
  return (
    (typeof v === "number" || (typeof v === "string" && v !== "")) &&
    Number.isInteger(Number(v)) &&
    Number(v) >= min &&
    Number(v) <= max
  );
}

export function isCallbackPref(v) {
  return CALLBACK_PREFS.includes(v);
}

export function isPaymentChannel(v) {
  // Public values only — venmo/zelle are internal, never offered (rule #5).
  return PAYMENT_CHANNELS.includes(v);
}

export function isAge(v) {
  return AGES.includes(String(v));
}

// --- form-level validators (return {field: msg} errors) ---------------------

// `booked` = the availability mirror's ranges (from the calendar step) — it is
// what lets a 5-night gap fill through; without it only 6–28 passes.
export function validateLead(form, booked) {
  const e = {};
  if (!isNonEmptyString(form.first_name)) e.first_name = "Required";
  if (!isNonEmptyString(form.last_name)) e.last_name = "Required";
  if (!isEmail(form.email)) e.email = "Enter a valid email";
  // Phone is required: every lifecycle touchpoint is double-bound SMS+email, so
  // a lead without a number silently degrades every later message (and a
  // call_me preference with no number is undeliverable). Same rule as the
  // reservation form (validateReservation).
  const phone = form.phone && form.phone.trim() ? normalizeE164(form.phone) : null;
  if (!phone) e.phone = "Enter a valid phone number";
  if (!isISODate(form.checkin_date)) e.checkin_date = "Pick a check-in date";
  if (!isISODate(form.checkout_date)) e.checkout_date = "Pick a check-out date";
  const nights = computeNights(form.checkin_date, form.checkout_date);
  if (form.checkin_date && form.checkout_date) {
    if (nights == null) {
      e.checkout_date = "Check-out must be after check-in";
    } else if (!isStayLengthOk(form.checkin_date, form.checkout_date, booked)) {
      // D19: outside 6-28 is a manual/contact path, not a hard rejection - but
      // we flag it so the UI can route to "contact us".
      e.nights = `Stays are ${MIN_NIGHTS}-${MAX_NIGHTS} nights (${GAP_FILL_HINT}). For other lengths, contact us.`;
    }
  }
  if (!isInt(form.adults, 1, 50)) e.adults = "At least 1 adult";
  if (!isInt(form.children, 0, 50)) e.children = "0 or more";
  if (!["yes", "no"].includes(form.wedding)) e.wedding = "Required";
  if (!isPaymentChannel(form.payment_channel)) e.payment_channel = "Select a payment method";
  if (!isCallbackPref(form.callback_pref)) e.callback_pref = "Choose a preference";
  if (form.sms_consent !== true) e.sms_consent = "Please consent to SMS updates about your booking";
  return { errors: e, phone, nights };
}

export function validateReservation(form, isVrbo) {
  const e = {};
  if (!isNonEmptyString(form.first_name)) e.first_name = "Required";
  if (!isNonEmptyString(form.last_name)) e.last_name = "Required";
  if (!isEmail(form.email)) e.email = "Enter a valid email";
  const phone = normalizeE164(form.phone);
  if (!phone) e.phone = "Enter a valid phone number";
  if (!isISODate(form.checkin_date)) e.checkin_date = "Required";
  if (!isISODate(form.checkout_date)) e.checkout_date = "Required";
  if (form.checkin_date && form.checkout_date) {
    const n = computeNights(form.checkin_date, form.checkout_date);
    // The dates are server truth (the quoted booking row, shown as text), so a
    // 5-night stay here already passed the gap-fill gate (Quote_isGapFill).
    if (n == null) e.checkout_date = "Check-out must be after check-in";
    else if (!inNightRange(n) && n !== GAP_FILL_NIGHTS) e.nights = `Stays are ${MIN_NIGHTS}–${MAX_NIGHTS} nights`;
  }
  if (!isNonEmptyString(form.address)) e.address = "Required";
  if (!["yes", "no"].includes(form.wedding)) e.wedding = "Required";
  if (!isInt(form.adults, 1, 50)) e.adults = "At least 1 adult";
  if (!isInt(form.children, 0, 50)) e.children = "0 or more";
  if (!isVrbo && !isPaymentChannel(form.payment_channel)) e.payment_channel = "Select a payment method";
  if (!form.idFile) e.idFile = "Upload a photo of your ID";
  if (form.sms_consent !== true) e.sms_consent = "Please consent to SMS updates about your booking";
  return { errors: e, phone };
}

export function validateArrival(form) {
  const e = {};
  if (!isNonEmptyString(form.booking_ref)) e.booking_ref = "Required";
  // guests === null/undefined: the party is frozen (stage sesamed+) — the
  // list is server truth, not submitted, so there is nothing to validate.
  if (form.guests == null) return { errors: e, guests: null };
  const allGuests = form.guests || [];
  if (allGuests.length < 1) e.guests = "Add at least one guest";
  if (allGuests.length > MAX_GUESTS) e.guests = `Maximum ${MAX_GUESTS} guests`;
  for (const g of allGuests) {
    if (!isNonEmptyString(g.name)) {
      e.guests = "Every guest needs a name (or remove the empty slot)";
      break;
    }
    if (!isAge(g.age)) {
      e.guests = "Every guest needs an age (or remove the empty slot)";
      break;
    }
  }
  return { errors: e, guests: allGuests };
}
